import React from 'react';
import { Alert, Button, Empty, Spin, Tag, Typography, message } from 'antd';
import { ReloadOutlined, CloudOutlined } from '@ant-design/icons';
import { useMeasurementReport } from './measurementReport/MeasurementReportContext';
import { MeasurementSectionShell, buildMeasurementSectionMeta } from './measurementReport/MeasurementSectionShell';
import { formatMeasurementTimestamp, formatUtcTimestamp } from '../../utils/measurementTime';

const { Text } = Typography;

interface CommonSectionProps {
    section: any;
}

export const ReportMeasurementWeather: React.FC<CommonSectionProps> = ({ section }) => {
    const { measurementId, measurement, weather, weatherContext, weatherLoading, refreshWeather } = useMeasurementReport();
    const sectionTitle = section?.title || 'Weather';
    const sectionMeta = buildMeasurementSectionMeta(measurement);

    const handleRefreshWeather = async () => {
        try {
            await refreshWeather();
            message.success('Weather data refreshed successfully');
        } catch (error) {
            message.error('Failed to refresh weather data');
        }
    };

    // Check if measurement has GPS coordinates
    const hasGPS = measurement?.geom?.coordinates && measurement.geom.coordinates.length >= 2;

    if (!measurementId) {
        return (
            <MeasurementSectionShell title={sectionTitle}>
                <Alert message="Select a measurement to view weather." type="info" showIcon />
            </MeasurementSectionShell>
        );
    }

    if (weatherLoading) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Spin tip="Loading weather..." />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (!weather) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    {hasGPS ? (
                        <>
                            <Empty 
                                description="Weather data not yet fetched" 
                                image={Empty.PRESENTED_IMAGE_SIMPLE} 
                            />
                            <Button 
                                type="primary" 
                                icon={<CloudOutlined />}
                                onClick={handleRefreshWeather}
                                loading={weatherLoading}
                                style={{ marginTop: 16 }}
                            >
                                Fetch Weather Data
                            </Button>
                        </>
                    ) : (
                        <Empty 
                            description="No GPS coordinates available for this measurement" 
                            image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        />
                    )}
                </div>
            </MeasurementSectionShell>
        );
    }

    return (
        <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                    {weatherContext && (
                        <div style={{ padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <strong>Weather conditions from:</strong> {formatMeasurementTimestamp(weatherContext.weatherDataFor, measurement, { seconds: false })}
                                {weatherContext.measurementDate && weatherContext.uploadDate && 
                                 weatherContext.measurementDate !== weatherContext.uploadDate && (
                                    <> <Tag color="blue" style={{ marginLeft: 8 }}>Uploaded later: {formatUtcTimestamp(weatherContext.uploadDate)}</Tag></>
                                )}
                            </Text>
                        </div>
                    )}
                </div>
                {hasGPS && (
                    <Button 
                        size="small" 
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshWeather}
                        loading={weatherLoading}
                        style={{ marginLeft: 8 }}
                        title="Refresh weather data (useful if GPS coordinates were updated)"
                    >
                        Refresh
                    </Button>
                )}
            </div>
            <div className="report-measurement-weather-hero">
                <div>
                    <div className="report-measurement-weather-temp">
                        {weather.temperature}°C
                    </div>
                    <div className="report-measurement-weather-subtitle">
                        Feels like {weather.feels_like}°C
                    </div>
                </div>
                <div className="report-measurement-chip-row">
                    <Tag color="blue">{weather.conditions}</Tag>
                    {weather.description ? <Text type="secondary">{weather.description}</Text> : null}
                </div>
            </div>

            <div className="report-measurement-weather-grid">
                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Humidity</span>
                    <span className="report-measurement-weather-value">{weather.humidity}%</span>
                </div>
                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Wind</span>
                    <span className="report-measurement-weather-value">
                        {weather.windSpeed} m/s {weather.windDirection ? `(${weather.windDirection}°)` : ''}
                    </span>
                </div>
                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Pressure</span>
                    <span className="report-measurement-weather-value">{weather.pressure} hPa</span>
                </div>
                <div className="report-measurement-info-card">
                    <span className="report-measurement-info-label">Recorded at</span>
                    <span className="report-measurement-weather-value">
                        {formatMeasurementTimestamp(weather.timestamp, measurement, { seconds: false })}
                    </span>
                </div>
                {(weather.rainfall !== null && weather.rainfall !== undefined) ? (
                    <div className="report-measurement-info-card">
                        <span className="report-measurement-info-label">Rainfall</span>
                        <span className="report-measurement-weather-value">
                            {weather.rainfall} mm
                        </span>
                    </div>
                ) : null}
            </div>
        </MeasurementSectionShell>
    );
};

export default ReportMeasurementWeather;
